import { computed, makeObservable, observable, runInAction } from 'mobx';
import { Validators } from 'core/services/Validators';
import { HttpClient } from 'shared/libs/http';
import { FormControl, FormGroup } from 'shared/libs/form';
import { AsyncState } from 'shared/libs/state';
import { LocalitySearchResponse } from 'candidate/types';
import { CandidateContactInfoStore } from './candidate-contact-info';
import {
   LoadUserProfileResponse,
   UploadCandidatePhotoRequestDto,
   UploadCandidatePhotoResponse,
   UpdateUserProfileRequestDto,
   UpdateUserProfileResponse,
   UserProfileData,
} from './feature-user-profile.types';

type CityOption = {
   id: number;
   name: string;
};

export class UserProfileStore {
   readonly loadUserProfileState = new AsyncState<LoadUserProfileResponse>();
   readonly uploadPhotoState = new AsyncState<UploadCandidatePhotoResponse>();
   readonly submitProfileState = new AsyncState<UpdateUserProfileResponse>();
   readonly candidateContactInfoStore = new CandidateContactInfoStore();
   readonly fromGroup = new FormGroup({
      father_name: new FormControl<string>('', [Validators.nameValidator]),
      first_name: new FormControl<string>('', [Validators.nameValidator]),
      last_name: new FormControl<string>('', [Validators.nameValidator]),
      gender: new FormControl<'male' | 'female' | ''>(''),
      birthday: new FormControl<string>(''),
      country_id: new FormControl<number | ''>(''),
      city_id: new FormControl<number | ''>(''),
   });

   userProfile: UserProfileData | null = null;
   cityOptions: CityOption[] = [];
   citiesLoading = false;
   avatarSyncLoading = false;
   temporaryProfileImage: string | null = null;
   pendingProfileImageBase64: string | null = null;
   private citySearchRequestSeq = 0;

   constructor() {
      makeObservable(this, {
         userProfile: observable,
         cityOptions: observable,
         citiesLoading: observable,
         avatarSyncLoading: observable,
         temporaryProfileImage: observable,
         pendingProfileImageBase64: observable,
         avatarPreviewImage: computed,
         hasPendingPhotoUpload: computed,
         profileSaving: computed,
      });
   }

   get avatarPreviewImage(): string | null {
      return this.temporaryProfileImage ?? this.userProfile?.profile_image ?? null;
   }

   get hasPendingPhotoUpload(): boolean | undefined {
      if (this.uploadPhotoState.status === 'pending') {
         return undefined;
      }
      return Boolean(this.pendingProfileImageBase64);
   }

   get profileSaving(): boolean {
      return this.submitProfileState.status === 'pending'
   }

   async loadUserProfile(): Promise<void> {
      await this.loadUserProfileState.run(
         () => HttpClient.get<LoadUserProfileResponse>('/users/editAccountCandidate'),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.userProfile = resp.response;
                  });

                  this.candidateContactInfoStore.hydrate(resp.response);

                  this.fromGroup.patchValue({
                     father_name: Validators.sanitizeName(resp.response.father_name ?? ''),
                     first_name: Validators.sanitizeName(resp.response.first_name ?? ''),
                     last_name: Validators.sanitizeName(resp.response.last_name ?? ''),
                     gender: this.toGenderValue(resp.response.gender),
                     birthday: resp.response.birthday ?? '',
                     country_id: resp.response.country_id ?? '',
                     city_id: resp.response.city_id ?? '',
                  });

                  this.cityOptions =
                     resp.response.city_id && resp.response.city
                        ? [
                             {
                                id: resp.response.city_id,
                                name: resp.response.city.label,
                             },
                          ]
                        : [];
               }
            },
         }
      );
   }

   async syncProfilePhoto(): Promise<void> {
      runInAction(() => {
         this.avatarSyncLoading = true;
      });

      try {
         const resp = await HttpClient.post<UploadCandidatePhotoResponse>(
            '/candidates/uploadPhoto',
            null
         );
         const currentProfile = this.userProfile;
         if (resp.status === 200 && currentProfile) {
            runInAction(() => {
               this.userProfile = {
                  ...currentProfile,
                  profile_image: resp.response.profile_image,
               };
            });
         }
      } finally {
         runInAction(() => {
            this.avatarSyncLoading = false;
         });
      }
   }

   async searchCities(query: string): Promise<void> {
      const countryId = this.fromGroup.controls.country_id.value;

      if (!countryId) {
         this.citySearchRequestSeq += 1;
         runInAction(() => {
            this.cityOptions = [];
            this.citiesLoading = false;
         });
         return;
      }

      const requestSeq = ++this.citySearchRequestSeq;
      runInAction(() => {
         this.citiesLoading = true;
      });

      try {
         const resp = await HttpClient.post<LocalitySearchResponse>('/locality/search', {
            country_id: countryId,
            query,
         });

         if (resp.status === 200 && requestSeq === this.citySearchRequestSeq) {
            runInAction(() => {
               this.cityOptions = resp.response.map((city) => ({
                  id: city.id,
                  name: city.name,
               }));
            });
         }
      } finally {
         if (requestSeq === this.citySearchRequestSeq) {
            runInAction(() => {
               this.citiesLoading = false;
            });
         }
      }
   }

   onCountrySelect(countryId: number): void {
      this.fromGroup.controls.country_id.setValue(countryId);
      this.fromGroup.controls.city_id.setValue('');
      this.cityOptions = [];
   }

   onCitySelect(cityId: number): void {
      this.fromGroup.controls.city_id.setValue(cityId);
   }

   setPersonalInfoName(field: 'first_name' | 'last_name' | 'father_name', value: string): void {
      this.fromGroup.controls[field].setValue(Validators.sanitizeName(value));
   }

   async selectPhotoFile(file: File): Promise<{ error?: string }> {
      try {
         if (file.size > 500 * 1024) {
            return { error: 'Image must be less than 500KB' };
         }

         const dimensions = await this.loadImageDimensions(file);
         if (dimensions.width < 250 || dimensions.height < 250) {
            return { error: 'Мінімальні розміри файлу повинні бути 250 * 250' };
         }

         const base64 = await this.convertToBase64(file);
         const temporaryImage = URL.createObjectURL(file);

         runInAction(() => {
            this.revokeTemporaryImageUrl();
            this.temporaryProfileImage = temporaryImage;
            this.pendingProfileImageBase64 = base64;
         });

         return {};
      } catch {
         return { error: 'Invalid image file' };
      }
   }

   resetSelectedPhoto(): void {
      this.revokeTemporaryImageUrl();
      this.temporaryProfileImage = null;
      this.pendingProfileImageBase64 = null;
   }

   async savePhoto(): Promise<{ ok: boolean; message: string }> {
      if (!this.pendingProfileImageBase64) {
         return { ok: false, message: 'Select an image before saving' };
      }

      const dto: UploadCandidatePhotoRequestDto = {
         profile_image: this.pendingProfileImageBase64,
      };

      const response = await this.uploadPhotoState.run(
         () => HttpClient.post<UploadCandidatePhotoResponse>('/candidates/uploadPhoto', dto),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  const currentProfile = this.userProfile;
                  if (currentProfile) {
                     runInAction(() => {
                        this.userProfile = {
                           ...currentProfile,
                           profile_image: resp.response.profile_image,
                        };
                     });
                  }

                  await this.syncProfilePhoto().catch(() => undefined);
                  this.resetSelectedPhoto();
               }
            },
         }
      );

      if (!response) {
         return { ok: false, message: this.uploadPhotoState.error?.message ?? 'Upload failed' };
      }

      if (response.status === 200) {
         return { ok: true, message: 'Profile photo saved' };
      }

      return { ok: false, message: response.message || 'Upload failed' };
   }

   async submitProfile(
      {
         onSuccess,
         onError,
      }: {
         onSuccess?: () => void | Promise<void>;
         onError?: (message: string, status?: number) => void | Promise<void>;
      } = {}
   ): Promise<UpdateUserProfileResponse | null> {
      this.fromGroup.validate();
      if (!this.fromGroup.valid) {
         this.markPersonalInfoAsTouched();
         return null;
      }

      const payload = this.buildUpdatePayload();

      return this.submitProfileState.run(
         () => HttpClient.post<UpdateUserProfileResponse>('/users/updateAccountCandidate', payload),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  await this.loadUserProfile();
                  await onSuccess?.();
               } else {
                  await onError?.(resp.message || 'Profile update failed', resp.status);
               }
            },
            onError: async (error) => {
               await onError?.(error.message || 'Profile update failed');
            },
         }
      );
   }

   private buildUpdatePayload(): UpdateUserProfileRequestDto {
      const contactStore = this.candidateContactInfoStore;

      return {
         first_name: this.fromGroup.controls.first_name.value.trim(),
         last_name: this.nullableText(this.fromGroup.controls.last_name.value),
         father_name: this.nullableText(this.fromGroup.controls.father_name.value),
         gender: this.toApiGender(this.fromGroup.controls.gender.value),
         birthday: this.nullableText(this.fromGroup.controls.birthday.value),
         country_id: this.nullableNumber(this.fromGroup.controls.country_id.value),
         city_id: this.nullableNumber(this.fromGroup.controls.city_id.value),
         contact: this.nullableText(contactStore.contact),
         email_address: contactStore.emailAddress.trim(),
         social_links: this.mapLinks(contactStore.socialRows),
         messenger_links: this.mapLinks(contactStore.messengerRows) ?? [],
      };
   }

   private markPersonalInfoAsTouched(): void {
      this.fromGroup.controls.first_name.markAsTouched();
      this.fromGroup.controls.last_name.markAsTouched();
      this.fromGroup.controls.father_name.markAsTouched();
   }

   private toGenderValue(gender: string | null): 'male' | 'female' | '' {
      const normalized = (gender ?? '').toLowerCase().trim();
      if (normalized === 'male' || normalized === 'man' || normalized === 'чоловік') {
         return 'male';
      }
      if (normalized === 'female' || normalized === 'woman' || normalized === 'жінка') {
         return 'female';
      }
      return '';
   }

   private toApiGender(gender: 'male' | 'female' | ''): 'woman' | 'man' | null {
      if (gender === 'male') {
         return 'man';
      }
      if (gender === 'female') {
         return 'woman';
      }
      return null;
   }

   private nullableText(value: string): string | null {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
   }

   private nullableNumber(value: number | ''): number | null {
      return value === '' ? null : value;
   }

   private mapLinks(
      rows: Array<{ link: string; type: string }>
   ): Array<{ link: string | null; type: string | null }> | null {
      const links = rows
         .map((row) => ({
            link: this.nullableText(row.link),
            type: this.nullableText(row.type),
         }))
         .filter((row) => row.link !== null || row.type !== null);

      return links.length ? links : null;
   }

   private async loadImageDimensions(file: File): Promise<{ width: number; height: number }> {
      const objectURL = URL.createObjectURL(file);
      try {
         const image = new Image();
         return await new Promise<{ width: number; height: number }>((resolve, reject) => {
            image.onload = () =>
               resolve({
                  width: image.width,
                  height: image.height,
               });
            image.onerror = () => reject(new Error('Cannot read image dimensions'));
            image.src = objectURL;
         });
      } finally {
         URL.revokeObjectURL(objectURL);
      }
   }

   private convertToBase64(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
         const fileReader = new FileReader();
         fileReader.readAsDataURL(file);
         fileReader.onload = () => {
            resolve(fileReader.result as string);
         };
         fileReader.onerror = (error) => {
            reject(error);
         };
      });
   }

   private revokeTemporaryImageUrl(): void {
      if (this.temporaryProfileImage?.startsWith('blob:')) {
         URL.revokeObjectURL(this.temporaryProfileImage);
      }
   }
}
